import { useRef, useCallback, useState } from 'react'

/**
 * Hook for real-time audio frequency analysis using Web Audio API.
 * Used to visualize microphone input during voice recording.
 */
export function useAudioAnalyser() {
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const animationRef = useRef<number>(0)
  const [frequencyData, setFrequencyData] = useState<Uint8Array>(new Uint8Array(32))
  const [isAnalysing, setIsAnalysing] = useState(false)

  const startAnalyser = useCallback((stream: MediaStream) => {
    // Clean up any existing analyser
    if (audioContextRef.current) {
      audioContextRef.current.close()
    }

    const audioContext = new AudioContext()
    const analyser = audioContext.createAnalyser()

    // Configure analyser for responsive visualization
    analyser.fftSize = 64  // Results in 32 frequency bins
    analyser.smoothingTimeConstant = 0.7  // Balance between responsive and smooth
    analyser.minDecibels = -90
    analyser.maxDecibels = -10

    const source = audioContext.createMediaStreamSource(stream)
    source.connect(analyser)
    // Don't connect to destination - we don't want to hear ourselves

    audioContextRef.current = audioContext
    analyserRef.current = analyser
    sourceRef.current = source
    setIsAnalysing(true)

    // Animation loop to update frequency data
    const updateFrequency = () => {
      if (!analyserRef.current) return

      const data = new Uint8Array(analyserRef.current.frequencyBinCount)
      analyserRef.current.getByteFrequencyData(data)
      setFrequencyData(new Uint8Array(data)) // Create new array to trigger re-render
      animationRef.current = requestAnimationFrame(updateFrequency)
    }

    updateFrequency()
  }, [])

  const stopAnalyser = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = 0
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect()
      sourceRef.current = null
    }

    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }

    analyserRef.current = null
    setIsAnalysing(false)
    setFrequencyData(new Uint8Array(32)) // Reset to zeros
  }, [])

  return {
    frequencyData,
    isAnalysing,
    startAnalyser,
    stopAnalyser
  }
}
